/**
 * Shared rental pricing utilities.
 *
 * Weekend rule (particuliers only):
 *   If the rental period is EXACTLY vendredi >= 12h → lundi < 12h,
 *   a flat rate of 1.5 rental days applies instead of per-day pricing.
 *   Professionals never get the weekend rate.
 */

/** Available time slots for rental start/end */
export const RENTAL_TIME_SLOTS = [
  "07:30", "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
  "11:00", "11:30", "12:00", "12:30", "13:00", "13:30", "14:00",
  "14:30", "15:00", "15:30", "16:00", "16:30", "17:00", "17:30", "18:00",
];

/**
 * Check if a rental period qualifies for the weekend forfait.
 *
 * Conditions:
 *   - Start day is Friday and start time >= 12:00
 *   - End day is Monday and end time <= 12:00
 *   (i.e. the rental spans exactly one weekend)
 */
export function isWeekendOnlyRental(
  startDate: string,
  endDate: string,
  startTime?: string,
  endTime?: string
): boolean {
  const sTime = startTime || "08:00";
  const eTime = endTime || "08:00";

  const start = new Date(`${startDate}T${sTime}:00`);
  const end = new Date(`${endDate}T${eTime}:00`);

  // Start must be Friday (day 5)
  if (start.getDay() !== 5) return false;
  // Start time must be >= 12:00
  if (start.getHours() < 12) return false;

  // End must be Monday (day 1)
  if (end.getDay() !== 1) return false;
  // End time must be <= 12:00
  if (end.getHours() > 12) return false;
  if (end.getHours() === 12 && end.getMinutes() > 0) return false;

  // Must be the NEXT Monday (not further)
  const diffDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
  if (diffDays > 3.5) return false;

  return true;
}

/**
 * Count total rental days (used for per-day pricing when weekend forfait doesn't apply).
 * When freeWeekends is true, Saturday and Sunday are excluded from the count.
 */
export function countRentalDays(
  startDate: string,
  endDate: string,
  startTime?: string,
  endTime?: string,
  freeWeekends?: boolean
): number {
  const sTime = startTime || "08:00";
  const eTime = endTime || "08:00";

  const start = new Date(`${startDate}T${sTime}:00`);
  const end = new Date(`${endDate}T${eTime}:00`);

  const diffMs = end.getTime() - start.getTime();
  if (diffMs <= 0) return 1;

  const totalDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (totalDays <= 0) return 1;

  if (!freeWeekends) return totalDays;

  // Count only weekdays (Mon-Fri)
  let weekdays = 0;
  const cursor = new Date(`${startDate}T00:00:00`);
  for (let i = 0; i < totalDays; i++) {
    const day = cursor.getDay(); // 0=Sun, 6=Sat
    if (day !== 0 && day !== 6) weekdays++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return weekdays <= 0 ? 1 : weekdays;
}

/**
 * Count weekend days in a rental period (for display purposes).
 */
export function countWeekendDays(
  startDate: string,
  endDate: string,
): number {
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
  let weekendDays = 0;
  const cursor = new Date(start);
  for (let i = 0; i < totalDays; i++) {
    const day = cursor.getDay();
    if (day === 0 || day === 6) weekendDays++;
    cursor.setDate(cursor.getDate() + 1);
  }
  return weekendDays;
}

/**
 * Compute the total HT for a rental line item.
 *
 * If isWeekendRental is true, the total is 1.5 days * quantity (flat rate).
 * Otherwise it's the standard per-day calculation.
 */
export function computeRentalTotalHt(
  pricePerDayHt: number,
  _weekendPriceHt: number | null,
  rentalDays: number,
  isWeekendRental: boolean,
  quantity: number
): number {
  if (isWeekendRental) {
    return pricePerDayHt * 1.5 * quantity;
  }
  return pricePerDayHt * rentalDays * quantity;
}
